import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InfoIcon } from 'lucide-react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { MultiSelect } from '@/components/ui/multi-select.tsx';
import { useSession } from '@/services/providers/use-session.ts';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { InviteUserFormData } from '@/features/users/schemas/invite-user.schema.ts';

export function CreateInviteUserPlantsForm() {
  const { t } = useTranslation();
  const { role: inviterRole } = useSession();
  const form = useFormContext<InviteUserFormData>();
  const inviteeRole = useWatch({ control: form.control, name: 'role' });

  const { data: plants, isLoading } = useGetPlantsQuery({ includeOverview: false });

  const plantOptions = useMemo(
    () =>
      (plants ?? []).map((plant) => ({
        label: plant.basicDetails?.name ?? plant.plantId,
        value: plant.plantId,
      })),
    [plants]
  );

  const showInfoBanner =
    inviteeRole === 'Company owner' || inviteeRole === 'Company administrator';

  const isInstallerWithZeroPlants =
    inviterRole === 'Installer' && !isLoading && plantOptions.length === 0;

  return (
    <div className="flex flex-col gap-3">
      <h2 className="font-semibold">{t('users.invite.steps.plants.title')}</h2>

      {showInfoBanner ? (
        <div className="bg-muted text-muted-foreground flex items-start gap-2 rounded-md border p-3 text-sm">
          <InfoIcon size={16} className="mt-0.5 shrink-0" />
          <p>{t('users.invite.steps.plants.allPlantsInfo')}</p>
        </div>
      ) : isInstallerWithZeroPlants ? (
        <div className="bg-muted text-muted-foreground rounded-md border p-3 text-sm">
          {t('users.invite.steps.plants.emptyState')}
        </div>
      ) : (
        <FormField
          control={form.control}
          name="plantIds"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('users.invite.fields.plants.label')}</FormLabel>
              <FormControl>
                <MultiSelect
                  options={plantOptions}
                  onValueChange={(values) => field.onChange(values)}
                  defaultValue={field.value ?? []}
                  placeholder={
                    isLoading
                      ? t('users.invite.fields.plants.loading')
                      : t('users.invite.fields.plants.placeholder')
                  }
                  variant="inverted"
                  maxCount={3}
                  disabled={isLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
